<template>
  <BaseCard class="spellCard" :disabled="disabled" :true-value="trueValue" :value="value" :checkbox="checkbox" :clickable="clickable" :check-icon="checkIcon">
    <template v-slot:title>{{ spellObj.name }}</template>
    <template v-slot:subtitle>{{ subtitle }}</template>
    <template v-slot:action>{{ actionType }}</template>
    <template v-if="spellObj.saves" v-slot:abilitySmall>{{ spellObj.saves.join(',') }}</template>
    <template v-slot:ability>+{{ ability }}</template>
    
    <Text v-if="description">{{ description }}</Text>
    <template v-if="spellObj.requiresRested || spellObj.saves || spellObj.bonus?.concentrate">
      <Tags>
        <span v-if="spellObj.requiresRested">Rested</span>
        <span v-if="spellObj.bonus?.concentrate">Concentration</span>
        <template v-for="skill in spellObj.saves" :key="skill">
          <span v-if="skillList[skill]">
            {{ skillList[skill].name }}
          </span>
        </template>
      </Tags>
    </template>
    <Damage v-if="spellObj.damage" :small="!!spellObj.description" class="damage">
      <template v-slot:damage>{{ spellObj.damage }}</template>
      <template v-if="spellObj.extraDamage" v-slot:extraDamage>{{ spellObj.extraDamage }}</template>
      <template v-slot:damageType>{{ spellObj.damageType }}</template>
    </Damage>
  </BaseCard>
</template>

<script>
import { defineComponent, computed, inject } from 'vue'

import BaseCard from '@/components/cards/BaseCard'
import Damage from '@/components/cards/content/Damage'
import Text from '@/components/cards/content/Text'
import Tags from '@/components/cards/content/Tags'

import cardProps from '@/helpers/cardProps'
import parseDescription from '@/helpers/parseDescription'

import spellList from '@/data/spells'
import skillList from '@/data/skills'

export default defineComponent({
  props: {
    spell: {
      type: String,
      required: true
    },

    ...cardProps
  },

  components: {
    BaseCard,
    Damage,
    Text,
    Tags
  },

  setup (props) {
    const computedCharacter = inject('computedCharacter')

    const spellObj = computed(() => spellList[props.spell])
    const description = computed(() => parseDescription(computedCharacter, spellObj.value))

    const subtitle = computed(() => {
      const parts = []

      if (spellObj.value.level === 0) parts.push('Cantrip')
      else parts.push(`Spell (lv. ${spellObj.value.level})`)

      if (spellObj.value.bonus?.duration) {
        parts.push(
          spellObj.value.bonus.duration === 'short'
            ? 'Short Duration'
            : 'Long Duration'
        )
      }

      return parts.join(', ')
    })

    const actionType = computed(() => {
      switch (spellObj.value.type) {
        case 'bonus':
          return 'Bonus Action'
        case 'reaction':
          return 'Reaction'
        case 'free':
          return 'Free Action'
      }
      return 'Action'
    })

    const ability = computed(() => computedCharacter 
      ? computedCharacter.characterClass.value.spellAbility 
      : 'SPA')

    return {
      ...computedCharacter,

      spellObj,
      subtitle,
      actionType,
      description,
      ability,

      // Data
      spellList,
      skillList
    }
  }
})
</script>

<style lang="sass">
.damage
  margin-top: -0.5rem
</style>
